import React from 'react';
import { Share2, Film, MessageCircle, Link2, Phone, Mic, Image, Video, Sticker, Sparkles, EyeOff, Bot, KeyRound } from 'lucide-react';
import { cn } from '@/lib/utils';
import { KIND_PLACEHOLDER, fmtClock } from '../../lib/chatsView';

/*
 * r16p (design 7.1, 7.6): one message of the transcript. Theirs on the left, ours on the right.
 *
 * Third-party text is PLAIN React text (whitespace-pre-wrap): no RichText, no HTML, no auto-linking, no link previews, no remote
 * images. A stranger's DM can carry a phishing link; it stays unclickable text here. Non-text items (shared posts, reels, voice,
 * photos...) are placeholders: no media in r16p, and never an <img> that points at Instagram (it would leak the viewer's IP).
 */
const KIND_ICON = {
  post_share: Share2, reel_share: Film, story_reply: MessageCircle, link: Link2, call: Phone, voice: Mic, photo: Image, video: Video,
  sticker: Sticker, gif: Sparkles, unsupported: EyeOff,
};

export function KindPlaceholder({ kind, className }) {
  const label = KIND_PLACEHOLDER[kind];
  if (!label) return null;
  const Icon = KIND_ICON[kind] || EyeOff;
  return (
    <span className={cn('inline-flex items-center gap-1.5 text-xs italic text-muted-foreground', className)} data-testid="kind-placeholder">
      <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
      {label}
    </span>
  );
}

export function SourceNote({ source }) {
  if (source === 'api') {
    return <span className="inline-flex items-center gap-1"><KeyRound className="h-3 w-3" aria-hidden />via the API</span>;
  }
  if (source === 'ai_edited' || source === 'ai') {
    return <span className="inline-flex items-center gap-1"><Bot className="h-3 w-3" aria-hidden />AI suggestion, edited by you</span>;
  }
  if (source === 'dashboard') return <span>from the dashboard</span>;
  return null;
}

export default function MessageBubble({ m }) {
  const ours = m.dir === 'out';
  const hasText = typeof m.text === 'string' && m.text !== '';
  const time = m.at && m.atPrecision === 'minute' ? fmtClock(m.at) : null;
  return (
    <div className={cn('flex', ours ? 'justify-end' : 'justify-start')} data-testid={`msg-${m.id}`} data-dir={m.dir}>
      <div className={cn('min-w-0 max-w-[85%] sm:max-w-[75%]')}>
        <div
          className={cn(
            'rounded-2xl px-3.5 py-2 text-sm break-words',
            ours ? 'rounded-br-md bg-primary/15 text-foreground ring-1 ring-primary/20' : 'rounded-bl-md bg-secondary/70 text-foreground',
            m.provisional && 'opacity-80',
          )}
        >
          {m.kind !== 'text' && <KindPlaceholder kind={m.kind} className={hasText ? 'mb-1 flex' : ''} />}
          {m.unsent ? (
            <span className="text-xs italic text-muted-foreground">This message was unsent.</span>
          ) : hasText ? (
            <div className="whitespace-pre-wrap break-words" data-testid="msg-text">{m.text}</div>
          ) : m.purged ? (
            <span className="text-xs italic text-muted-foreground">Text removed after the retention period.</span>
          ) : m.kind === 'text' ? (
            <span className="text-xs italic text-muted-foreground">No text stored for this message.</span>
          ) : null}
        </div>
        {(time || m.edited || (ours && m.source && m.source !== 'worker')) && (
          <div className={cn('mt-0.5 flex flex-wrap items-center gap-x-2 px-1 text-[11px] text-muted-foreground', ours && 'justify-end')}>
            {m.edited && <span>edited</span>}
            {ours && <SourceNote source={m.source} />}
            {time && <span className="tnum">{time}</span>}
          </div>
        )}
      </div>
    </div>
  );
}
